import { Cell, Grid, Direction, Position } from '../types/game';

export const GRID_SIZE = 4;
export const WINNING_VALUE = 2048;

export function createEmptyGrid(): Grid {
    return Array(GRID_SIZE).fill(null)
        .map(() => Array(GRID_SIZE).fill(null));
}

export function getEmptyPositions(grid: Grid): Position[] {
    const positions: Position[] = [];
    for (let row = 0; row < GRID_SIZE; row++) {
        for (let col = 0; col < GRID_SIZE; col++) {
            if (grid[row][col] === null) {
                positions.push({ row, col });
            }
        }
    }
    return positions;
}

export function addRandomTile(grid: Grid): Grid {
    const newGrid = grid.map(row => [...row]);
    const emptyPositions = getEmptyPositions(newGrid);

    if (emptyPositions.length === 0) return newGrid;

    const { row, col } = emptyPositions[Math.floor(Math.random() * emptyPositions.length)];
    newGrid[row][col] = Math.random() < 0.9 ? 2 : 4;

    return newGrid;
}

export function rotateGrid(grid: Grid, direction: Direction): Grid {
    let newGrid = grid.map(row => [...row]);

    switch (direction) {
        case 'left':
            return newGrid;
        case 'right':
            return newGrid.map(row => row.reverse());
        case 'up':
            return transpose(newGrid);
        case 'down':
            return transpose(newGrid).map(row => row.reverse());
    }
}

export function transpose(grid: Grid): Grid {
    return grid[0].map((_, i) => grid.map(row => row[i]));
}

export function mergeLine(line: Cell[]): { line: Cell[], score: number } {
    const newLine: Cell[] = [];
    let score = 0;
    let index = 0;

    // Remove nulls and create a new array
    const numbers = line.filter(cell => cell !== null);

    while (index < numbers.length) {
        if (index + 1 < numbers.length && numbers[index] === numbers[index + 1]) {
            const mergedValue = numbers[index]! * 2;
            newLine.push(mergedValue);
            score += mergedValue;
            index += 2;
        } else {
            newLine.push(numbers[index]!);
            index++;
        }
    }

    // Pad with nulls to maintain grid size
    while (newLine.length < GRID_SIZE) {
        newLine.push(null);
    }

    return { line: newLine, score };
}

export function moveGrid(grid: Grid, direction: Direction): { grid: Grid, score: number } {
    let rotatedGrid = rotateGrid(grid, direction);
    let totalScore = 0;

    // Merge each row
    rotatedGrid = rotatedGrid.map(row => {
        const { line, score } = mergeLine(row);
        totalScore += score;
        return line;
    });

    // Rotate back
    switch (direction) {
        case 'left':
            return { grid: rotatedGrid, score: totalScore };
        case 'right':
            return { grid: rotatedGrid.map(row => row.reverse()), score: totalScore };
        case 'up':
            return { grid: transpose(rotatedGrid), score: totalScore };
        case 'down':
            return { grid: transpose(rotatedGrid.map(row => row.reverse())), score: totalScore };
    }
}

export function isGameOver(grid: Grid): boolean {
    // Check for empty cells
    if (getEmptyPositions(grid).length > 0) return false;

    // Check for possible merges
    for (let row = 0; row < GRID_SIZE; row++) {
        for (let col = 0; col < GRID_SIZE; col++) {
            const current = grid[row][col];

            // Check right neighbor
            if (col < GRID_SIZE - 1 && current === grid[row][col + 1]) return false;

            // Check bottom neighbor
            if (row < GRID_SIZE - 1 && current === grid[row + 1][col]) return false;
        }
    }

    return true;
}

export function hasWon(grid: Grid): boolean {
    return grid.some(row => row.some(cell => cell === WINNING_VALUE));
} 